import { useEffect, useRef } from 'react';
import { getPlatform, isNativeApp, triggerHaptic } from './nativeBridge';

// Swipe sideways anywhere on a page to go back (right in left-to-right layouts,
// left in right-to-left ones). An arrow slides in from the edge while the finger
// moves and fills in once letting go will go back.
//
// The gesture stays out of the way of anything that already uses sideways
// movement: a horizontal scroller that still has content to scroll back to,
// sliders, text fields, canvases, and anything floating above the page
// (sheets, drawers, the nav bars). A screen that is itself a fixed full-screen
// layer, like an open chat, opts back in with data-swipe-back="page".

const LOCK_PX = 12;
const LEAVE_PX = 90;
const FLICK_PX = 40;
const FLICK_SPEED = 0.5; // px per ms
const INDICATOR_TRAVEL = 56;
// The screen edges belong to the system's own back gesture (Android, and Safari in a
// browser tab); starting there would go back twice. The iOS app has no such gesture.
const SYSTEM_EDGE_PX = 24;
const edgesAreOurs = () => isNativeApp() && getPlatform() === 'ios';

const canStartFrom = (target) => {
  for (let el = target; el && el !== document.body; el = el.parentElement) {
    if (el.dataset?.noSwipeBack != null) return false;
    if (el.dataset?.swipeBack === 'page') return true;
    if (/^(INPUT|TEXTAREA|SELECT|CANVAS)$/.test(el.tagName) || el.isContentEditable) return false;
    const style = getComputedStyle(el);
    if (style.position === 'fixed') return false;
    // A sideways scroller only wins while it is scrolled away from its start; at the start
    // the same swipe has nothing to scroll, so it goes back
    if (/(auto|scroll)/.test(style.overflowX) && el.scrollWidth > el.clientWidth + 1 && Math.abs(el.scrollLeft) > 1) return false;
  }
  return true;
};

/**
 * @param {{ enabled: boolean, onBack: () => void, indicatorRef: React.RefObject<HTMLElement> }} options
 */
export function useSwipeBack({ enabled, onBack, indicatorRef }) {
  const onBackRef = useRef(onBack);
  onBackRef.current = onBack;

  useEffect(() => {
    if (!enabled) return undefined;
    let swipe = null;

    const showIndicator = (distance, y, sign) => {
      const indicator = indicatorRef.current;
      if (!indicator) return;
      const armed = distance > LEAVE_PX;
      const travel = Math.min(INDICATOR_TRAVEL, distance * 0.6);
      indicator.style.transition = 'none';
      indicator.style.top = `${y - 20}px`;
      indicator.style[sign === 1 ? 'left' : 'right'] = '0px';
      indicator.style[sign === 1 ? 'right' : 'left'] = 'auto';
      indicator.style.opacity = String(Math.min(1, distance / 60));
      indicator.style.transform = `translateX(${sign * (travel - 44)}px) scale(${armed ? 1.15 : 1}) scaleX(${sign})`;
      indicator.dataset.armed = armed ? 'true' : 'false';
    };

    const hideIndicator = () => {
      const indicator = indicatorRef.current;
      if (!indicator) return;
      indicator.style.transition = 'opacity 150ms ease-out';
      indicator.style.opacity = '0';
    };

    const onStart = (e) => {
      swipe = null;
      if (e.touches.length !== 1 || !canStartFrom(e.target)) return;
      const touch = e.touches[0];
      const fromEdge = Math.min(touch.clientX, window.innerWidth - touch.clientX) < SYSTEM_EDGE_PX;
      if (fromEdge && !edgesAreOurs()) return;
      const sign = getComputedStyle(document.body).direction === 'rtl' ? -1 : 1;
      swipe = { x: touch.clientX, y: touch.clientY, at: Date.now(), sign, direction: null, distance: 0 };
    };

    const onMove = (e) => {
      if (!swipe) return;
      const touch = e.touches[0];
      const dx = (touch.clientX - swipe.x) * swipe.sign;
      const dy = touch.clientY - swipe.y;
      if (swipe.direction == null) {
        if (Math.abs(dx) < LOCK_PX && Math.abs(dy) < LOCK_PX) return;
        // Decided once per gesture, so scrolling a page never turns into going back
        swipe.direction = dx > 0 && Math.abs(dx) > Math.abs(dy) * 1.5 ? 'back' : 'scroll';
      }
      if (swipe.direction !== 'back') return;
      const wasArmed = swipe.distance > LEAVE_PX;
      swipe.distance = Math.max(0, dx);
      if (!wasArmed && swipe.distance > LEAVE_PX) triggerHaptic('selection');
      showIndicator(swipe.distance, touch.clientY, swipe.sign);
    };

    const onEnd = () => {
      const done = swipe;
      swipe = null;
      if (!done || done.direction !== 'back') return;
      hideIndicator();
      const flicked = done.distance > FLICK_PX && done.distance / Math.max(1, Date.now() - done.at) > FLICK_SPEED;
      if (done.distance > LEAVE_PX || flicked) {
        triggerHaptic('light');
        onBackRef.current();
      }
    };

    document.addEventListener('touchstart', onStart, { passive: true });
    document.addEventListener('touchmove', onMove, { passive: true });
    document.addEventListener('touchend', onEnd, { passive: true });
    document.addEventListener('touchcancel', onEnd, { passive: true });
    return () => {
      hideIndicator();
      document.removeEventListener('touchstart', onStart);
      document.removeEventListener('touchmove', onMove);
      document.removeEventListener('touchend', onEnd);
      document.removeEventListener('touchcancel', onEnd);
    };
  }, [enabled, indicatorRef]);
}

export default useSwipeBack;
